import { mergeClasses } from '@fluentui/react-components';
import { IPIcon, IPNav, IPNavItem, IPPopover, IPPopoverSurface, IPText, isIPIconName } from 'baseComponents';
import { useGlobalTextStyles } from 'globalStyles';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { navIconSize, navRailClass, navRowClass, useNavMainStyles } from './navMainStylesV9';
import { INavModelItem } from './navModel';

//
// The callout beside a pinned group header: the group's name over its pages, drawn with the menu's own
// rows (a Fluent v9 Popover). It does not know the model; the rail hands it the header and hears back
// which page was picked.
//

export interface INavHintCalloutProps {
  /** The header row the callout is anchored to. A new element moves the open callout. */
  target: HTMLElement;
  /** The pinned group whose pages the callout lists. Its label is the overline. */
  header: INavModelItem;
  /** The current page, accented in the list. */
  selectedKey: string;
  /** Move keyboard focus into the list when the callout opens. */
  focusOnOpen: boolean;
  onSelect: (id: string) => void;
  onDismiss: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export interface INavHintCalloutHandle {
  /** Moves keyboard focus to the first page. */
  focus: () => void;
}

/** An event inside the menu is the user working the menu: it does not close the callout. */
const isInsideMenu = (target: EventTarget | null | undefined): boolean =>
  target instanceof Element && target.closest(`.${navRailClass}`) !== null;

const NavHintCalloutV9 = forwardRef<INavHintCalloutHandle, INavHintCalloutProps>((props, ref) => {
  const styles = useNavMainStyles();
  const textStyles = useGlobalTextStyles();
  const surfaceRef = useRef<HTMLDivElement>(null);

  const focusFirstPage = () => {
    surfaceRef.current?.querySelector<HTMLElement>(`.${navRowClass}`)?.focus();
  };

  useImperativeHandle(ref, () => ({ focus: focusFirstPage }), []);

  // A focus request that arrives while the callout is already open, or the one that opens it.
  useEffect(() => {
    if (props.focusOnOpen) {
      window.setTimeout(focusFirstPage, 0);
    }
  }, [props.focusOnOpen]);

  return (
    <IPPopover
      open={true}
      positioning={{ target: props.target, position: 'after', align: 'top', offset: 4 }}
      onOpenChange={(ev, data) => {
        if (!data.open && !isInsideMenu(ev?.target)) {
          props.onDismiss();
        }
      }}
      closeOnScroll
    >
      <IPPopoverSurface
        ref={surfaceRef}
        className={styles.calloutSurface}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') {
            ev.stopPropagation();
            props.onDismiss();
            props.target.focus();
          }
        }}
      >
        <IPText size={200} className={mergeClasses(styles.calloutOverline, textStyles.subtle)}>
          {props.header.label}
        </IPText>
        <IPNav
          className={styles.nav}
          selectedValue={props.selectedKey}
          onNavItemSelect={(_ev, data) => props.onSelect(String(data.value))}
        >
          {(props.header.children ?? []).map((child) => (
            <IPNavItem
              key={child.id}
              value={child.id}
              className={mergeClasses(navRowClass, styles.row, child.className)}
              icon={isIPIconName(child.iconName) ? <IPIcon name={child.iconName} fontSize={navIconSize} /> : undefined}
            >
              {child.label}
            </IPNavItem>
          ))}
        </IPNav>
      </IPPopoverSurface>
    </IPPopover>
  );
});

NavHintCalloutV9.displayName = 'NavHintCalloutV9';

export default NavHintCalloutV9;
